import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, LoadOptions } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { mapSubscriberGroupFromWire, mapSubscriberGroupToWire } from './utils/map-subscriber-group';
import { subscriberGroupsTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestSubscriberGroup, ReceiveSubscriberGroup, FailSubscriberGroup,
  RequestSaveSubscriberGroup, ReceiveSaveSubscriberGroup, FailSaveSubscriberGroup,
  UpdateItem, ChangeLanguage, CopyLanguageToAll, ClearForm, UpdateValidation, ChangeLongDescription,
  CopyLongDescriptionToAll, UpdateExternalMemberItem, RemoveExternalMember,
  RemoveAllExternalMembers, ResetAllExternalMemberValidation, AddExternalMember, ResetValidation,
  RequestSubscriberGroupOptions, ReceiveSubscriberGroupOptions, FailSubscriberGroupOptions,
  RequestSubscriberGroupItemDelete, ReceiveSubscriberGroupItemDelete, FailSubscriberGroupItemDelete,
  UpdateMergeSubscriberGroup
} from './action-types';
import { api } from './api';
import { getBackRoute } from './route';
import {
  ExternalMemberValue, SubscriberGroupItem, SubscriberGroupItemValidation
} from './types-store';
import { SubscriberGroupActionType } from 'app/area/subscriber-groups/form/types';
import { MergeSubscriberGroupState } from 'app/area/subscriber-groups/shared/merge-subscriber-group/types-store';
import { subscriberGroupOptionsMapped } from 'app/area/subscriber-groups/shared/merge-subscriber-group/mapping';
import { sharedApi } from 'app/area/subscriber-groups/shared/api';

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  createSubscriberGroup: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => dispatch(new ReceiveSubscriberGroup(mapSubscriberGroupFromWire({}))),

  loadSubscriberGroup: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestSubscriberGroup());

      try {

        const subscriberGroupWired = await api.loadSubscriberGroup(id);
        const subscriberGroupMapped = mapSubscriberGroupFromWire(subscriberGroupWired);

        dispatch(new ReceiveSubscriberGroup(subscriberGroupMapped));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.subscriberGroups,
            translationId: subscriberGroupsTranslation.no_access_to_subscriber_groups
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailSubscriberGroup());
      }
    },

  deleteSubscriberGroup: (id: number, successMessage: string, reassignReferencesToCode?: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestSubscriberGroupItemDelete(id));

      try {
        await sharedApi.deleteMergeSubscriberGroup(id, reassignReferencesToCode);

        navigate.toAdminSubscriberGroupList();

        dispatch(new ReceiveSubscriberGroupItemDelete(id));
        dispatch(new UpdateMergeSubscriberGroup(undefined));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailSubscriberGroupItemDelete(id));

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  saveSubscriberGroup: (
    actionType: SubscriberGroupActionType,
    subscriberGroupItem: SubscriberGroupItem,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = subscriberGroupItem?.id;

      dispatch(new RequestSaveSubscriberGroup());

      try {
        const subscriberGroupWire = mapSubscriberGroupToWire(subscriberGroupItem);

        if (actionType === SubscriberGroupActionType.edit) {
          await api.updateSubscriberGroup(id, subscriberGroupWire);
        } else {
          await api.addSubscriberGroup(subscriberGroupWire);
        }

        dispatch(new ReceiveSaveSubscriberGroup());

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {
        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        dispatch(new FailSaveSubscriberGroup());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  updateExternalMemberItem: (externalMemberValue: ExternalMemberValue): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateExternalMemberItem(externalMemberValue));
    },

  removeExternalMember: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveExternalMember(id));
    },

  removeAllExternalMembers: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveAllExternalMembers());
      dispatch(new ResetAllExternalMemberValidation());
    },

  addExternalMember: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new AddExternalMember());
    },

  updateMergeSubscriberGroup: (mergeSubscriberGroup: MergeSubscriberGroupState) => (dispatch: any, getState: any) => {
    dispatch(new UpdateMergeSubscriberGroup(mergeSubscriberGroup));
  },

  loadSubscriberGroupOptions: (loadOptionsParams: LoadOptions): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestSubscriberGroupOptions());

      try {
        const subscriberGroupOptions = await sharedApi.loadSubscriberGroupOptions(loadOptionsParams);
        dispatch(new ReceiveSubscriberGroupOptions(subscriberGroupOptionsMapped(subscriberGroupOptions.data.results)));
      } catch (err) {
        dispatch(new FailSubscriberGroupOptions());
      }
    },

  resetValidation: (id: number, key?: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch: any, getState: any) => {
      dispatch(new ResetValidation(id, key));
    },

  updateItem: (item: Partial<SubscriberGroupItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  changeLongDescription: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLongDescription(languageId, text));
    },

  copyLongDescriptionToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLongDescriptionToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: SubscriberGroupItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
